import { useState } from 'react'
import cx from 'classnames'
import { Workspace } from 'types'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { Close, LinkAngled } from 'blaze-ui/icons'
import { MailOpened } from 'blaze-ui/icons'
import InviteByEmail from './InviteByEmail'
import ImportEmails from './ImportEmails'
import Styles from './InviteTeam.module.scss'
import { Heading } from 'almanac-ui/Heading/Heading'
import { InviteByLink } from './InviteByLink'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from './components/MobileHeader/MobileHeader'
import { useTierUsageLimits } from 'hooks/useTierUsageLimits'

export type Tab = 'email' | 'link'

interface InviteTeamProps {
  onClose: () => void
  workspace: Workspace
}

const InviteTeam = ({ onClose, workspace }: InviteTeamProps) => {
  const [showImport, setShowImport] = useState(false)
  const [currentTab, setCurrentTab] = useState<Tab>('email')
  const { data: currentPlan } = useCurrentPlan()

  const showImportScreen = () => {
    setShowImport(true)
    trackEventInHeap(HeapEventType.ImportContacts, { action: 'google_contact_import_started', type: '' })
  }
  const { atLimit, nextTierPlan, currentTierPlan } = useTierUsageLimits({
    limitKey: 'seats',
  })

  const shouldEnforceSeatLimit = atLimit && nextTierPlan && currentTierPlan

  return (
    <>
      <ResponsiveDesign.Mobile>
        <MobileHeader currentTab={currentTab} setCurrentTab={setCurrentTab} />
        <div className={Styles.mobileContentContainer}>
          {currentTab === 'email' && <InviteByEmail onClose={onClose} workspace={workspace} />}
          {currentTab === 'link' && showImport && (
            <ImportEmails onClose={onClose} setShowImport={setShowImport} workspace={workspace} />
          )}
          {currentTab === 'link' && !showImport && currentPlan && (
            <InviteByLink showImportScreen={showImportScreen} currentPlan={currentPlan} onClose={onClose} />
          )}
        </div>
      </ResponsiveDesign.Mobile>
      <ResponsiveDesign.Desktop>
        <div className={Styles.header}>
          <Heading level={4}>Invite Your Team</Heading>
          <div className={Styles.headerTabs}>
            <Button
              variant={currentTab === 'email' ? 'secondary' : 'tertiary'}
              size="sm"
              frontIcon={MailOpened}
              className={cx(Styles.headerTab, currentTab === 'email' && Styles.headerTabSelected)}
              onPress={() => setCurrentTab('email')}
            >
              Invite by Email
            </Button>
            <Button
              variant={currentTab === 'link' ? 'secondary' : 'tertiary'}
              size="sm"
              frontIcon={LinkAngled}
              className={cx(Styles.headerTab, currentTab === 'link' && Styles.headerTabSelected)}
              onPress={() => setCurrentTab('link')}
              isDisabled={!!shouldEnforceSeatLimit}
            >
              Invite by Link
            </Button>
          </div>
          <IconButton icon={Close} size="sm" variant="tertiary" onPress={onClose} />
        </div>
        <div className={Styles.tabContentArea}>
          {currentTab === 'email' && <InviteByEmail onClose={onClose} workspace={workspace} />}
          {currentTab === 'link' && showImport && (
            <ImportEmails onClose={onClose} setShowImport={setShowImport} workspace={workspace} />
          )}
          {currentTab === 'link' && !showImport && currentPlan && (
            <InviteByLink showImportScreen={showImportScreen} currentPlan={currentPlan} onClose={onClose} />
          )}
        </div>
      </ResponsiveDesign.Desktop>
    </>
  )
}

export default InviteTeam
